import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useDogs } from "@/context/DogsContext";
import { SectionTitle } from "@/components/app/Bits";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Heart, ChevronLeft, ChevronRight, Baby, DollarSign, Activity, Eye } from "lucide-react";
import { format, parseISO, addDays, differenceInCalendarDays } from "date-fns";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, ReferenceLine, ReferenceArea, PieChart, Pie, Cell } from "recharts";

// Display labels for backend's BREEDING_METHODS keys. Keep order in sync
// with the canonical list so the form's <select> matches /api response.
const METHOD_LABELS = {
  live_cover: "Live cover",
  ai_vaginal: "AI — vaginal",
  ai_tci: "AI — transcervical (TCI)",
  ai_surgical: "AI — surgical",
  fresh_semen: "Fresh semen",
  chilled_semen: "Chilled semen",
  frozen_semen: "Frozen semen",
  ivf_embryo_transfer: "IVF / embryo transfer",
  other: "Other",
};
const METHOD_ORDER = Object.keys(METHOD_LABELS);

// Human labels + inline hint of what each cost bucket is for. Keys must match
// backend COST_CATEGORIES verbatim.
const COST_LABELS = {
  progesterone_smear: "Progesterone / vaginal smear",
  vet_visit:          "Vet visit (ultrasound, palpation, x-ray)",
  semen_shipping:     "Semen shipping",
  semen_collection:   "Semen collection (AI)",
  travel:             "Hotel / travel (live cover)",
  surgical_ai:        "Surgical insemination",
  other:              "Other",
};
const COST_ORDER = Object.keys(COST_LABELS);

// Currency formatter — reads locale from the browser so a UK breeder sees £,
// a US breeder sees $. Falls back to a plain number on failure.
const fmtMoney = (n) => {
  const num = Number(n) || 0;
  try {
    return num.toLocaleString(undefined, { style: "currency", currency: currencyFromLocale() });
  } catch {
    return num.toFixed(2);
  }
};
function currencyFromLocale() {
  try {
    const region = (Intl.DateTimeFormat().resolvedOptions().locale || "en-US").split("-")[1] || "US";
    const map = { US: "USD", GB: "GBP", CA: "CAD", AU: "AUD", NZ: "NZD", IE: "EUR", DE: "EUR", FR: "EUR", ES: "EUR", IT: "EUR", NL: "EUR", SE: "SEK", NO: "NOK", DK: "DKK", CH: "CHF", JP: "JPY" };
    return map[region] || "USD";
  } catch { return "USD"; }
}

// Heuristic if the breeder hasn't recorded peak fertility days yet — most
// bitches are fertile roughly days 9–14 from cycle start. Used only when no
// explicit peak is set, and only for the visual estimate.
const DEFAULT_PEAK_OFFSETS = [9, 14];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function blankCycle(dogs) {
  const firstBitch = (dogs || []).find((d) => (d.sex || "").toLowerCase() === "female");
  return {
    bitch_dog_id: firstBitch?.id || "",
    bitch_name: firstBitch?.name || "",
    cycle_start: new Date().toISOString().slice(0, 10),
    peak_fertility_start: "",
    peak_fertility_end: "",
    breeding_dates: [],
    breeding_method: "",
    stud_name: firstBitch?.name ? "" : "",
    notes: "",
    is_observation_only: false,
    progesterone_tests: [],
    costs: [],
  };
}

export default function HeatCyclesPage() {
  const { dogs } = useDogs();
  const navigate = useNavigate();
  const [year, setYear] = useState(new Date().getFullYear());
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(blankCycle(dogs));
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newBreedDate, setNewBreedDate] = useState("");

  const load = async (y) => {
    try {
      const { data } = await api.get("/heat-cycles", { params: { year: y } });
      setItems(data.items || []);
    } catch (err) { toast.error("Could not load heat cycles"); }
  };

  useEffect(() => { load(year); }, [year]);
  useEffect(() => { if (!editingId) setForm(blankCycle(dogs)); /* eslint-disable-next-line */ }, [dogs.length]);

  const bitches = useMemo(() => (dogs || []).filter((d) => (d.sex || "").toLowerCase() === "female"), [dogs]);

  const pickBitch = (id) => {
    const d = dogs.find((x) => x.id === id);
    setForm((f) => ({ ...f, bitch_dog_id: id || "", bitch_name: d?.name || f.bitch_name }));
  };

  const addBreedDate = () => {
    if (!newBreedDate) return;
    setForm((f) => {
      if (f.breeding_dates.includes(newBreedDate)) return f;
      return { ...f, breeding_dates: [...f.breeding_dates, newBreedDate].sort() };
    });
    setNewBreedDate("");
  };
  const removeBreedDate = (d) =>
    setForm((f) => ({ ...f, breeding_dates: f.breeding_dates.filter((x) => x !== d) }));

  // ------- progesterone tests (cap 6 in the UI) -------
  const MAX_PROG_TESTS = 6;
  // Stable per-row key so React reconciles inputs correctly when a middle
  // row is removed. Extra `_key` field is silently dropped by the backend's
  // _clean_heat_cycle_body (only date/value_ng_ml/notes are accepted).
  const newKey = () => {
    try { return crypto.randomUUID(); } catch { return `k_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`; }
  };
  const addProgTest = () =>
    setForm((f) => f.progesterone_tests.length >= MAX_PROG_TESTS ? f : ({
      ...f,
      progesterone_tests: [
        ...f.progesterone_tests,
        { _key: newKey(), date: new Date().toISOString().slice(0, 10), value_ng_ml: "", notes: "" },
      ],
    }));
  const updateProgTest = (idx, patch) =>
    setForm((f) => ({
      ...f,
      progesterone_tests: f.progesterone_tests.map((t, i) => (i === idx ? { ...t, ...patch } : t)),
    }));
  const removeProgTest = (idx) =>
    setForm((f) => ({
      ...f,
      progesterone_tests: f.progesterone_tests.filter((_, i) => i !== idx),
    }));

  // ------- costs -------
  const addCost = () =>
    setForm((f) => ({
      ...f,
      costs: [
        ...f.costs,
        { _key: newKey(), date: new Date().toISOString().slice(0, 10), category: "progesterone_smear", amount: "", notes: "" },
      ],
    }));
  const updateCost = (idx, patch) =>
    setForm((f) => ({
      ...f,
      costs: f.costs.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
    }));
  const removeCost = (idx) =>
    setForm((f) => ({ ...f, costs: f.costs.filter((_, i) => i !== idx) }));

  const formCostTotal = useMemo(
    () => (form.costs || []).reduce((s, c) => s + (Number(c.amount) || 0), 0),
    [form.costs],
  );

  const submit = async (e) => {
    e.preventDefault();
    // When female dogs exist, require the cycle to be linked to a dog profile
    // for new records; editing existing legacy name-only records is still
    // allowed (users are nudged via the amber hint to link it).
    if (bitches.length > 0 && !editingId && !form.bitch_dog_id) {
      toast.error("Pick which dog this cycle is for");
      return;
    }
    if (!form.bitch_name && !form.bitch_dog_id) {
      toast.error("Bitch name or linked dog is required");
      return;
    }
    if (!form.cycle_start) { toast.error("Cycle start is required"); return; }
    // Auto-fill bitch_name from the linked dog when it's blank — belt-and-
    // braces so the backend snapshot stays useful even if the user renames
    // the dog later.
    let payload = { ...form };
    if (payload.bitch_dog_id && !payload.bitch_name) {
      const d = dogs.find((x) => x.id === payload.bitch_dog_id);
      if (d) payload.bitch_name = d.name;
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/heat-cycles/${editingId}`, payload);
        toast.success("Heat cycle updated");
      } else {
        await api.post("/heat-cycles", payload);
        toast.success("Heat cycle logged");
      }
      setForm(blankCycle(dogs));
      setEditingId(null);
      load(year);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const editCycle = (c) => {
    setEditingId(c.id);
    setForm({
      bitch_dog_id: c.bitch_dog_id || "",
      bitch_name: c.bitch_name || "",
      cycle_start: c.cycle_start || "",
      peak_fertility_start: c.peak_fertility_start || "",
      peak_fertility_end: c.peak_fertility_end || "",
      breeding_dates: c.breeding_dates || [],
      breeding_method: c.breeding_method || "",
      stud_name: c.stud_name || "",
      notes: c.notes || "",
      is_observation_only: !!c.is_observation_only,
      // Attach stable per-row keys so reconciliation survives middle-row
      // removes when the user edits an existing cycle.
      progesterone_tests: (c.progesterone_tests || []).map((t) => ({ ...t, _key: t._key || newKey() })),
      costs: (c.costs || []).map((k) => ({ ...k, _key: k._key || newKey() })),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => { setEditingId(null); setForm(blankCycle(dogs)); };

  const removeCycle = async (id) => {
    if (!window.confirm("Delete this heat cycle?")) return;
    try {
      await api.delete(`/heat-cycles/${id}`);
      load(year);
    } catch (err) { toast.error("Could not delete"); }
  };

  // Hand a logged cycle to the litter system. Pre-fills dam/stud/bred_on
  // from the cycle, then drops the user straight onto the new litter page.
  const createLitterFromCycle = async (cycle) => {
    if (!(cycle.breeding_dates || []).length) {
      toast.error("Add at least one breeding date before creating a litter.");
      return;
    }
    try {
      const { data } = await api.post(`/heat-cycles/${cycle.id}/create-litter`);
      toast.success(`Litter created — expected whelp ~ ${data.expected_whelp_day_63}`);
      navigate(`/breeder/litters/${data.litter_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create litter");
    }
  };

  return (
    <div className="space-y-8" data-testid="heat-cycles-page">
      <SectionTitle overline="Breeder" title="Heat cycles" />

      {/* Form */}
      <Card className="border-border shadow-none" data-testid="heat-cycle-form-card">
        <CardContent className="p-6">
          <form onSubmit={submit} className="space-y-4" data-testid="heat-cycle-form">
            {/* Observation-only toggle. Some breeders track heat cycles just
                to monitor regularity or health, without any breeding intent.
                Flipping this on hides breeding-only fields and blocks the
                Create-litter affordance on the row. Everything else (dates,
                progesterone, costs) stays available so health-only monitoring
                still gets its value. */}
            <label
              className="flex items-start gap-3 rounded-md border border-border bg-secondary/40 p-3 cursor-pointer select-none"
              data-testid="observation-only-toggle-label"
            >
              <input
                type="checkbox"
                checked={!!form.is_observation_only}
                onChange={(e) => setForm({ ...form, is_observation_only: e.target.checked })}
                className="mt-1 h-4 w-4 accent-primary"
                data-testid="observation-only-toggle"
              />
              <div className="min-w-0">
                <p className="text-sm font-medium">Observation only — not planning to breed</p>
                <p className="text-xs text-muted-foreground leading-snug">
                  Log this heat for cycle-tracking / health only. Stud, breeding method, and breeding-dates fields will hide, and the row won&apos;t offer a Create-litter shortcut. You can flip this off later.
                </p>
              </div>
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-1">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Bitch *</Label>
                {bitches.length > 0 ? (
                  <>
                    <select
                      value={form.bitch_dog_id}
                      onChange={(e) => pickBitch(e.target.value)}
                      className="mt-1.5 w-full border border-input rounded-md bg-background px-3 py-2 text-sm h-10"
                      required={!editingId || !!form.bitch_dog_id || !form.bitch_name}
                      data-testid="heat-cycle-bitch-select"
                    >
                      <option value="">— Select a dog —</option>
                      {bitches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                    {editingId && !form.bitch_dog_id && form.bitch_name ? (
                      <p className="text-[11px] text-amber-700 mt-1.5 leading-snug" data-testid="heat-cycle-unlinked-hint">
                        This cycle isn&apos;t linked to a dog profile (name: <strong>{form.bitch_name}</strong>). Pick a dog above to link it.
                      </p>
                    ) : (
                      <p className="text-[11px] text-muted-foreground mt-1.5 leading-snug">Linked to the dog&apos;s profile so heat cycles show on Overview.</p>
                    )}
                  </>
                ) : (
                  <>
                    <Input
                      value={form.bitch_name}
                      onChange={(e) => setForm({ ...form, bitch_name: e.target.value, bitch_dog_id: "" })}
                      placeholder="Bitch name"
                      className="mt-1.5"
                      required
                      data-testid="heat-cycle-bitch-name"
                    />
                    <p className="text-[11px] text-muted-foreground mt-1.5 leading-snug" data-testid="heat-cycle-no-female-hint">
                      Tip: add a dog with sex &ldquo;Female&rdquo; on Overview and this cycle will link to its profile automatically.
                    </p>
                  </>
                )}
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Cycle start (day 1) *</Label>
                <Input type="date" value={form.cycle_start} onChange={(e) => setForm({ ...form, cycle_start: e.target.value })} required className="mt-1.5" data-testid="heat-cycle-start" />
              </div>
              {!form.is_observation_only && (
                <div>
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">Stud name</Label>
                  <Input value={form.stud_name} onChange={(e) => setForm({ ...form, stud_name: e.target.value })} placeholder="Optional" className="mt-1.5" data-testid="heat-cycle-stud" />
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Peak fertility start</Label>
                <Input type="date" value={form.peak_fertility_start} onChange={(e) => setForm({ ...form, peak_fertility_start: e.target.value })} className="mt-1.5" data-testid="heat-cycle-peak-start" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Peak fertility end</Label>
                <Input type="date" value={form.peak_fertility_end} onChange={(e) => setForm({ ...form, peak_fertility_end: e.target.value })} className="mt-1.5" data-testid="heat-cycle-peak-end" />
              </div>
              {!form.is_observation_only && (
                <div>
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">Breeding method</Label>
                  <select
                    value={form.breeding_method}
                    onChange={(e) => setForm({ ...form, breeding_method: e.target.value })}
                    className="mt-1.5 w-full border border-input rounded-md bg-background px-3 py-2 text-sm h-10"
                    data-testid="heat-cycle-method"
                  >
                    <option value="">— Choose —</option>
                    {METHOD_ORDER.map((k) => <option key={k} value={k}>{METHOD_LABELS[k]}</option>)}
                  </select>
                </div>
              )}
            </div>

            {!form.is_observation_only && (
              <div data-testid="heat-cycle-breeding-dates-block">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Breeding dates (bitch covered / inseminated)</Label>
                <div className="flex flex-wrap items-center gap-2 mt-1.5">
                  <Input type="date" value={newBreedDate} onChange={(e) => setNewBreedDate(e.target.value)} className="w-44" data-testid="heat-cycle-breed-date-input" />
                  <Button type="button" variant="outline" size="sm" onClick={addBreedDate} disabled={!newBreedDate} data-testid="heat-cycle-breed-date-add">
                    <Plus size={13} className="mr-1" /> Add date
                  </Button>
                  <div className="flex flex-wrap gap-1.5" data-testid="heat-cycle-breed-dates">
                    {form.breeding_dates.map((d) => (
                      <span key={d} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 border border-primary/30 text-primary text-xs" data-testid={`heat-cycle-breed-date-${d}`}>
                        {format(parseISO(d), "MMM d")}
                        <button type="button" onClick={() => removeBreedDate(d)} aria-label={`remove ${d}`} className="hover:bg-primary/20 rounded-full p-0.5">
                          <Trash2 size={10} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                  <Activity size={12} /> Progesterone tests (up to {6})
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addProgTest}
                  disabled={(form.progesterone_tests || []).length >= 6}
                  data-testid="prog-add"
                >
                  <Plus size={13} className="mr-1" /> Add test
                </Button>
              </div>
              {(form.progesterone_tests || []).length === 0 ? (
                <p className="text-xs text-muted-foreground mt-2">
                  Log progesterone readings (ng/mL) across the heat — the chart lights up automatically once you have two or more.
                </p>
              ) : (
                <div className="mt-3 space-y-2" data-testid="prog-list">
                  {(form.progesterone_tests || []).map((t, i) => (
                    <div key={t._key || i} className="grid grid-cols-12 gap-2 items-center bg-secondary/30 rounded-md p-2" data-testid={`prog-row-${i}`}>
                      <Input
                        type="date"
                        value={t.date || ""}
                        onChange={(e) => updateProgTest(i, { date: e.target.value })}
                        className="col-span-4 h-9"
                        data-testid={`prog-date-${i}`}
                      />
                      <div className="col-span-4 relative">
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          value={t.value_ng_ml ?? ""}
                          onChange={(e) => updateProgTest(i, { value_ng_ml: e.target.value })}
                          placeholder="value"
                          className="h-9 pr-14"
                          data-testid={`prog-value-${i}`}
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-wider text-muted-foreground">ng/mL</span>
                      </div>
                      <Input
                        value={t.notes || ""}
                        onChange={(e) => updateProgTest(i, { notes: e.target.value })}
                        placeholder="lab / notes"
                        className="col-span-3 h-9"
                        data-testid={`prog-notes-${i}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeProgTest(i)}
                        className="col-span-1 flex items-center justify-center h-9 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                        aria-label="Remove progesterone test"
                        data-testid={`prog-remove-${i}`}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {(form.progesterone_tests || []).filter((t) => t.date && t.value_ng_ml !== "" && t.value_ng_ml != null).length >= 2 && (
                <div className="mt-4">
                  <ProgesteroneChart tests={form.progesterone_tests} />
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                  <DollarSign size={12} /> Costs
                </Label>
                <Button type="button" variant="outline" size="sm" onClick={addCost} data-testid="cost-add">
                  <Plus size={13} className="mr-1" /> Add cost
                </Button>
              </div>
              {(form.costs || []).length === 0 ? (
                <p className="text-xs text-muted-foreground mt-2" data-testid="cost-breakdown-empty">
                  Track vet visits, semen shipping, hotel/travel, and surgical fees per cycle. Running total appears here + on the cycle list.
                </p>
              ) : (
                <>
                  <div className="mt-3 space-y-2" data-testid="cost-list">
                    {(form.costs || []).map((c, i) => (
                      <div key={c._key || i} className="grid grid-cols-12 gap-2 items-center bg-secondary/30 rounded-md p-2" data-testid={`cost-row-${i}`}>
                        <Input
                          type="date"
                          value={c.date || ""}
                          onChange={(e) => updateCost(i, { date: e.target.value })}
                          className="col-span-3 h-9"
                          data-testid={`cost-date-${i}`}
                        />
                        <select
                          value={c.category || "other"}
                          onChange={(e) => updateCost(i, { category: e.target.value })}
                          className="col-span-4 h-9 border border-border rounded-md bg-card px-2 text-sm"
                          data-testid={`cost-category-${i}`}
                        >
                          {COST_ORDER.map((k) => (
                            <option key={k} value={k}>{COST_LABELS[k]}</option>
                          ))}
                        </select>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={c.amount ?? ""}
                          onChange={(e) => updateCost(i, { amount: e.target.value })}
                          placeholder="0.00"
                          className="col-span-2 h-9 text-right"
                          data-testid={`cost-amount-${i}`}
                        />
                        <Input
                          value={c.notes || ""}
                          onChange={(e) => updateCost(i, { notes: e.target.value })}
                          placeholder="note"
                          className="col-span-2 h-9"
                          data-testid={`cost-notes-${i}`}
                        />
                        <button
                          type="button"
                          onClick={() => removeCost(i)}
                          className="col-span-1 flex items-center justify-center h-9 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                          aria-label="Remove cost"
                          data-testid={`cost-remove-${i}`}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
                    <CostBreakdownDonut costs={form.costs} total={formCostTotal} />
                    <div className="flex sm:flex-col items-baseline sm:items-end justify-between sm:justify-center gap-1">
                      <span className="text-xs uppercase tracking-wider text-muted-foreground">Running total</span>
                      <span className="font-display font-bold text-3xl text-primary" data-testid="cost-running-total">{fmtMoney(formCostTotal)}</span>
                      <span className="text-[11px] text-muted-foreground">{(form.costs || []).length} entr{(form.costs || []).length === 1 ? "y" : "ies"}</span>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Progesterone readings, vet observations, stud arrangements..." className="mt-1.5" data-testid="heat-cycle-notes" />
            </div>

            <div className="flex items-center gap-2">
              <Button type="submit" disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="heat-cycle-save">
                {saving ? "Saving…" : (editingId ? "Save changes" : "Log heat cycle")}
              </Button>
              {editingId && (
                <Button type="button" variant="ghost" onClick={cancelEdit} data-testid="heat-cycle-cancel">Cancel</Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Yearly visualization */}
      <YearlyTimeline year={year} setYear={setYear} cycles={items} />

      {/* List */}
      <Card className="border-border shadow-none" data-testid="heat-cycle-list-card">
        <CardContent className="p-6">
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-3">Cycles in {year}</p>
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="heat-cycle-empty">No heat cycles logged for {year}.</p>
          ) : (
            <ul className="divide-y divide-border border border-border rounded-md" data-testid="heat-cycle-list">
              {items.map((c) => {
                const linked = !!c.linked_litter_id;
                const isObs = !!c.is_observation_only;
                const canCreate = !linked && !isObs && (c.breeding_dates || []).length > 0;
                return (
                <li key={c.id} className="p-3 flex items-start justify-between gap-3" data-testid={`heat-cycle-row-${c.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium flex items-center gap-2 flex-wrap">
                      <span>{c.bitch_name} <span className="text-muted-foreground font-normal">· starts {format(parseISO(c.cycle_start), "MMM d, yyyy")}</span></span>
                      {isObs ? (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground text-[10px] uppercase tracking-wider font-semibold border border-border"
                          data-testid={`heat-cycle-observation-pill-${c.id}`}
                          title="Logged for cycle-tracking / health only"
                        >
                          <Eye size={10} /> Observation
                        </span>
                      ) : null}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {isObs
                        ? "Observation only"
                        : (c.breeding_method ? (METHOD_LABELS[c.breeding_method] || c.breeding_method) : "Method not set")}
                      {!isObs && c.stud_name ? ` · stud: ${c.stud_name}` : ""}
                      {!isObs && c.breeding_dates?.length > 0 ? ` · ${c.breeding_dates.length} breeding ${c.breeding_dates.length === 1 ? "day" : "days"}` : ""}
                      {(c.progesterone_tests || []).length > 0 ? ` · ${c.progesterone_tests.length} prog test${c.progesterone_tests.length === 1 ? "" : "s"}` : ""}
                      {(c.costs || []).length > 0 ? (
                        <>
                          {" · "}
                          <span className="text-primary font-semibold" data-testid={`heat-cycle-total-${c.id}`}>
                            {fmtMoney((c.costs || []).reduce((s, cc) => s + (Number(cc.amount) || 0), 0))}
                          </span>
                        </>
                      ) : null}
                    </p>
                    {c.notes && <p className="text-xs text-foreground/70 mt-1 whitespace-pre-wrap">{c.notes}</p>}
                    {linked && (
                      <LitterOutcomeLink cycle={c} onOpen={() => navigate(`/breeder/litters/${c.linked_litter_id}`)} />
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {canCreate && (
                      <button
                        onClick={() => createLitterFromCycle(c)}
                        className="px-2.5 py-1 rounded-md text-xs font-medium bg-primary/10 text-primary border border-primary/30 hover:bg-primary/20 flex items-center gap-1"
                        data-testid={`heat-cycle-create-litter-${c.id}`}
                        title="Create a litter pre-filled with this cycle's dam, stud and first breeding date"
                      >
                        <Baby size={12} /> Create litter
                      </button>
                    )}
                    <button onClick={() => editCycle(c)} className="px-2 py-1 hover:bg-muted rounded-md text-xs text-muted-foreground hover:text-foreground" data-testid={`heat-cycle-edit-${c.id}`}>
                      Edit
                    </button>
                    <button onClick={() => removeCycle(c.id)} className="p-1.5 hover:bg-muted rounded-md text-muted-foreground hover:text-destructive" data-testid={`heat-cycle-delete-${c.id}`} aria-label="Delete">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ----------------------------------------------------------------
// Yearly timeline — 12 month strips with a horizontal track per
// unique bitch. Each cycle is plotted as a soft proestrus/estrus
// band, peak-fertility filled, breeding days marked as dots.
// ----------------------------------------------------------------
function YearlyTimeline({ year, setYear, cycles }) {
  // Group by bitch for clean lanes. Sort by name so order is stable.
  const lanes = useMemo(() => {
    const m = new Map();
    for (const c of cycles) {
      const k = c.bitch_dog_id || c.bitch_name;
      if (!m.has(k)) m.set(k, { label: c.bitch_name || "Unnamed", cycles: [] });
      m.get(k).cycles.push(c);
    }
    return [...m.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [cycles]);

  const yearStart = useMemo(() => new Date(year, 0, 1), [year]);
  const totalDays = (year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)) ? 366 : 365;

  // Regularity thresholds (days between successive cycle starts). Typical
  // domestic bitch = one heat every ~6 months. Anything <5 mo = short gap,
  // >7 mo = long gap. Excluding short-cycle breeds (basenji, whippet) here
  // — user can eyeball the "Avg" and decide.
  const GAP_SHORT_MAX = 150;    // days
  const GAP_LONG_MIN  = 210;    // days
  const classifyGap = (days) => {
    if (days == null) return null;
    if (days < GAP_SHORT_MAX) return "short";
    if (days > GAP_LONG_MIN)  return "long";
    return "normal";
  };

  // Map a YYYY-MM-DD inside the year to a 0..100 percentage.
  const dayPct = (iso) => {
    if (!iso) return null;
    try {
      const d = parseISO(iso);
      const idx = differenceInCalendarDays(d, yearStart);
      if (idx < 0 || idx > totalDays) return null;
      return (idx / totalDays) * 100;
    } catch { return null; }
  };

  // Compute the cycle's proestrus/estrus band: from cycle_start to peak_end+3
  // (covers the bleeding + fertile window). Default to 21 days if no peak.
  const bandFor = (c) => {
    const startPct = dayPct(c.cycle_start);
    if (startPct == null) return null;
    let endIso = c.peak_fertility_end;
    if (!endIso) {
      try { endIso = format(addDays(parseISO(c.cycle_start), 21), "yyyy-MM-dd"); } catch { endIso = c.cycle_start; }
    } else {
      try { endIso = format(addDays(parseISO(endIso), 3), "yyyy-MM-dd"); } catch { /* keep */ }
    }
    const endPct = dayPct(endIso);
    if (endPct == null) return null;
    return { startPct, endPct: Math.max(endPct, startPct + 1) };
  };

  // Peak fertility band — drawn on top, filled darker. Falls back to a
  // heuristic if the breeder hasn't recorded peak dates yet.
  const peakBandFor = (c) => {
    let startIso = c.peak_fertility_start;
    let endIso = c.peak_fertility_end;
    if (!startIso || !endIso) {
      try {
        const s = parseISO(c.cycle_start);
        startIso = startIso || format(addDays(s, DEFAULT_PEAK_OFFSETS[0]), "yyyy-MM-dd");
        endIso = endIso || format(addDays(s, DEFAULT_PEAK_OFFSETS[1]), "yyyy-MM-dd");
      } catch { return null; }
    }
    const startPct = dayPct(startIso);
    const endPct = dayPct(endIso);
    if (startPct == null || endPct == null) return null;
    return { startPct, endPct: Math.max(endPct, startPct + 0.5), estimated: !c.peak_fertility_start || !c.peak_fertility_end };
  };

  return (
    <Card className="border-border shadow-none" data-testid="heat-cycle-timeline">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-center justify-between gap-2 mb-5 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Yearly view</p>
            <p className="font-display text-lg font-bold tracking-tight mt-0.5">All cycles in {year}</p>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setYear(year - 1)} className="p-2 rounded-md border border-border hover:bg-muted" data-testid="heat-cycle-year-prev" aria-label="Previous year">
              <ChevronLeft size={14} />
            </button>
            <span className="px-3 text-sm font-semibold tabular-nums" data-testid="heat-cycle-year-label">{year}</span>
            <button onClick={() => setYear(year + 1)} className="p-2 rounded-md border border-border hover:bg-muted" data-testid="heat-cycle-year-next" aria-label="Next year">
              <ChevronRight size={14} />
            </button>
          </div>
        </div>

        {/* Month header — equal-width grid; the lanes below use absolute % so this just labels the axis. */}
        <div className="grid grid-cols-12 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1 select-none">
          {MONTHS.map((m) => <div key={m} className="px-1">{m}</div>)}
        </div>

        {lanes.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center" data-testid="heat-cycle-timeline-empty">
            No cycles in {year} yet. Log one above to see it plotted here.
          </p>
        ) : (
          <div className="space-y-3" data-testid="heat-cycle-timeline-lanes">
            {lanes.map((lane) => {
              // Compute inter-cycle gaps (in days) from every cycle's
              // previous_cycle_start. Backend supplies that field even when
              // the previous cycle happened last year, so first-in-year
              // gaps count too.
              const gaps = lane.cycles
                .map((c) => {
                  if (!c.previous_cycle_start || !c.cycle_start) return null;
                  try {
                    const days = differenceInCalendarDays(parseISO(c.cycle_start), parseISO(c.previous_cycle_start));
                    if (days <= 0) return null;
                    return { cycle: c, days, cls: classifyGap(days) };
                  } catch { return null; }
                })
                .filter(Boolean);
              const nShort = gaps.filter((g) => g.cls === "short").length;
              const nLong = gaps.filter((g) => g.cls === "long").length;
              const avgGap = gaps.length ? Math.round(gaps.reduce((s, g) => s + g.days, 0) / gaps.length) : null;
              const laneCls = nShort > 0 || nLong > 0
                ? (nShort + nLong >= 2 ? "text-rose-600" : "text-amber-600")
                : "text-emerald-600";

              return (
              <div key={lane.label} data-testid={`heat-cycle-lane-${lane.label}`}>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <Heart size={12} className="text-primary" />
                  <p className="text-xs font-semibold">{lane.label}</p>
                  {avgGap != null ? (
                    <span
                      className={`text-[10px] uppercase tracking-wider font-semibold ${laneCls}`}
                      title="Average interval between cycle starts"
                      data-testid={`heat-cycle-lane-avg-${lane.label}`}
                    >
                      avg gap {avgGap}d
                    </span>
                  ) : null}
                  {nShort > 0 ? (
                    <span
                      className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-700 border border-amber-500/30 font-semibold"
                      title={`${nShort} short gap${nShort === 1 ? "" : "s"} (<5 months)`}
                      data-testid={`heat-cycle-lane-short-${lane.label}`}
                    >
                      {nShort} short
                    </span>
                  ) : null}
                  {nLong > 0 ? (
                    <span
                      className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-rose-500/10 text-rose-700 border border-rose-500/30 font-semibold"
                      title={`${nLong} long gap${nLong === 1 ? "" : "s"} (>7 months)`}
                      data-testid={`heat-cycle-lane-long-${lane.label}`}
                    >
                      {nLong} long
                    </span>
                  ) : null}
                </div>
                {/* Track */}
                <div className="relative h-6 bg-muted/40 rounded-md border border-border overflow-hidden">
                  {/* In-year gap connectors — thin colored lines linking a cycle
                      to its predecessor when both fall inside the current
                      year. Cross-year gaps only contribute to the summary. */}
                  {gaps.map((g) => {
                    const startPct = dayPct(g.cycle.previous_cycle_start);
                    const endPct = dayPct(g.cycle.cycle_start);
                    if (startPct == null || endPct == null) return null;
                    const color = g.cls === "short" ? "bg-amber-500" : g.cls === "long" ? "bg-rose-500" : "bg-emerald-500";
                    return (
                      <div
                        key={`gap-${g.cycle.id}`}
                        className={`absolute top-1/2 h-[2px] -translate-y-1/2 rounded ${color} opacity-70`}
                        style={{ left: `${startPct}%`, width: `${Math.max(0.4, endPct - startPct)}%` }}
                        title={`Gap: ${g.days} days (${g.cls})`}
                        data-testid={`heat-cycle-gap-${g.cycle.id}`}
                      />
                    );
                  })}
                  {lane.cycles.map((c) => {
                    const band = bandFor(c);
                    if (!band) return null;
                    const peak = peakBandFor(c);
                    return (
                      <div key={c.id}>
                        {/* proestrus + estrus band */}
                        <div
                          className="absolute top-0 bottom-0 bg-primary/15 border-l-2 border-r-2 border-primary/40"
                          style={{ left: `${band.startPct}%`, width: `${band.endPct - band.startPct}%` }}
                          title={`${c.bitch_name}: ${c.cycle_start} → ${band.endPct.toFixed(0)}% of year`}
                          data-testid={`heat-cycle-band-${c.id}`}
                        />
                        {/* peak fertility — darker overlay */}
                        {peak && (
                          <div
                            className={`absolute top-1 bottom-1 ${peak.estimated ? "bg-primary/40" : "bg-primary"} rounded`}
                            style={{ left: `${peak.startPct}%`, width: `${peak.endPct - peak.startPct}%` }}
                            title={peak.estimated ? "Estimated peak fertility (days 9–14)" : "Recorded peak fertility"}
                            data-testid={`heat-cycle-peak-${c.id}`}
                          />
                        )}
                        {/* breeding day markers */}
                        {(c.breeding_dates || []).map((d) => {
                          const p = dayPct(d);
                          if (p == null) return null;
                          return (
                            <div
                              key={d}
                              className="absolute top-0 bottom-0 w-[3px] bg-foreground"
                              style={{ left: `calc(${p}% - 1px)` }}
                              title={`Bred ${d}`}
                              data-testid={`heat-cycle-bred-marker-${c.id}-${d}`}
                            />
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
              );
            })}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 mt-5 pt-4 border-t border-border text-[11px] text-muted-foreground" data-testid="heat-cycle-legend">
          <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-2 bg-primary/15 border border-primary/40 rounded-sm" /> Cycle window</span>
          <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-2 bg-primary rounded-sm" /> Peak fertility (recorded)</span>
          <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-2 bg-primary/40 rounded-sm" /> Peak fertility (estimated d 9&ndash;14)</span>
          <span className="flex items-center gap-1.5"><span className="inline-block w-[3px] h-3 bg-foreground" /> Breeding day</span>
          <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-[2px] bg-emerald-500 rounded" /> Normal gap (5–7 mo)</span>
          <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-[2px] bg-amber-500 rounded" /> Short (&lt;5 mo)</span>
          <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-[2px] bg-rose-500 rounded" /> Long (&gt;7 mo)</span>
        </div>
      </CardContent>
    </Card>
  );
}


// Compact "what happened with the linked litter" line. Reads the enriched
// litter_summary the backend attaches in /heat-cycles. Status-aware so the
// breeder can see at a glance whether a cycle resulted in puppies, is mid-
// gestation, slipped, or hasn't been recorded yet.
function LitterOutcomeLink({ cycle, onOpen }) {
  const s = cycle.litter_summary;
  if (!s) {
    return (
      <button
        type="button"
        onClick={onOpen}
        className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline"
        data-testid={`heat-cycle-open-litter-${cycle.id}`}
      >
        <Baby size={11} /> Open linked litter →
      </button>
    );
  }
  if (s.status === "missing") {
    return <p className="text-xs text-destructive mt-2" data-testid={`heat-cycle-outcome-${cycle.id}`}>Linked litter was deleted.</p>;
  }

  let label;
  if (s.status === "whelped") {
    const wd = s.born_on ? format(parseISO(s.born_on), "MMM d") : "";
    label = s.puppy_count > 0
      ? `${s.puppy_count} ${s.puppy_count === 1 ? "puppy" : "puppies"} whelped${wd ? ` ${wd}` : ""}`
      : `Whelped${wd ? ` ${wd}` : ""} — no puppies recorded yet`;
  } else if (s.status === "gestating") {
    const days = s.expected_whelp
      ? differenceInCalendarDays(parseISO(s.expected_whelp), new Date())
      : null;
    if (days == null) label = "In gestation";
    else if (days === 0) label = "Expected to whelp today";
    else if (days > 0) label = `Whelping in ~${days} ${days === 1 ? "day" : "days"}`;
    else label = `Whelp window closing (${-days} ${days === -1 ? "day" : "days"} past day 63)`;
  } else if (s.status === "overdue") {
    label = "Overdue — verify pregnancy";
  } else {
    label = "Litter planning";
  }

  const tone = s.status === "whelped"
    ? "bg-primary/10 border-primary/30 text-primary"
    : s.status === "overdue"
      ? "bg-destructive/10 border-destructive/30 text-destructive"
      : "bg-secondary text-secondary-foreground border-border";

  return (
    <button
      type="button"
      onClick={onOpen}
      className={`mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-full border text-xs font-medium hover:opacity-80 transition-opacity ${tone}`}
      data-testid={`heat-cycle-outcome-${cycle.id}`}
      title="Open linked litter"
    >
      <Baby size={11} /> {label} →
    </button>
  );
}


// -----------------------------------------------------------------
// ProgesteroneChart — small Recharts line chart. Values shown in
// ng/mL. Three clinical reference lines: LH surge (~2 ng/mL),
// ovulation (~5 ng/mL), fertilization window (~20 ng/mL). A shaded
// band highlights the 10–20 ng/mL optimal breeding window, and a
// callout below the chart names concrete dates that hit or bracket
// that window (linear interp between adjacent tests when no direct
// hit exists). Rendered inline in the form as soon as ≥2 tests have
// both a date + value.
// -----------------------------------------------------------------
const OPTIMAL_LOW = 10;
const OPTIMAL_HIGH = 20;

function ProgesteroneChart({ tests }) {
  const data = useMemo(() => {
    return (tests || [])
      .filter((t) => t.date && t.value_ng_ml !== "" && t.value_ng_ml != null && !isNaN(Number(t.value_ng_ml)))
      .map((t) => ({
        date: t.date,
        // Use MM-DD as the tick — dense enough to fit in ~450px, still readable.
        label: format(parseISO(t.date), "MMM d"),
        value: Number(t.value_ng_ml),
        notes: t.notes || "",
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [tests]);

  const yMax = Math.max(25, ...data.map((d) => d.value)) * 1.1;

  // Compute suggested-breeding dates. Two paths:
  //  1) Direct hit — any test whose value is inside [10, 20]. Report those days.
  //  2) Crossing — no direct hit but a pair of adjacent tests bracket a
  //     threshold (10 or 20). Linear-interp the day the curve crossed and
  //     report that as an estimate.
  const suggestions = useMemo(() => {
    if (data.length < 1) return { hits: [], crossings: [] };
    const hits = data
      .filter((d) => d.value >= OPTIMAL_LOW && d.value <= OPTIMAL_HIGH)
      .map((d) => ({ label: d.label, date: d.date, value: d.value }));

    const crossings = [];
    for (let i = 0; i < data.length - 1; i += 1) {
      const a = data[i], b = data[i + 1];
      // Only interp when adjacent points are on opposite sides of a threshold.
      for (const threshold of [OPTIMAL_LOW, OPTIMAL_HIGH]) {
        const belowA = a.value < threshold, belowB = b.value < threshold;
        if (belowA === belowB) continue;
        // Linear interp between a and b — day = daysBetween(a,b) * (threshold - a.value) / (b.value - a.value)
        const totalDays = differenceInCalendarDays(parseISO(b.date), parseISO(a.date));
        if (totalDays <= 0 || b.value === a.value) continue;
        const frac = (threshold - a.value) / (b.value - a.value);
        const offsetDays = Math.round(totalDays * frac);
        const crossDate = addDays(parseISO(a.date), offsetDays);
        crossings.push({
          threshold,
          date: format(crossDate, "yyyy-MM-dd"),
          label: format(crossDate, "MMM d"),
          direction: belowA ? "rising" : "falling",
        });
      }
    }
    return { hits, crossings };
  }, [data]);

  const suggestionLine = useMemo(() => {
    const { hits, crossings } = suggestions;
    if (hits.length) {
      const dates = hits.map((h) => `${h.label} (${h.value} ng/mL)`).join(", ");
      return `Optimal breeding window hit on ${dates}.`;
    }
    // Prefer the rising 10 → crossing (i.e. when curve first entered the window)
    const risingLow = crossings.find((c) => c.threshold === OPTIMAL_LOW && c.direction === "rising");
    const risingHigh = crossings.find((c) => c.threshold === OPTIMAL_HIGH && c.direction === "rising");
    if (risingLow && risingHigh) {
      return `Estimated optimal window: ${risingLow.label} → ${risingHigh.label}.`;
    }
    if (risingLow) return `Estimated crossing into the window around ${risingLow.label}.`;
    if (risingHigh) return `Estimated crossing above the window around ${risingHigh.label}.`;
    if (data.length && data[data.length - 1].value < OPTIMAL_LOW) {
      return "Progesterone still below the optimal window. Next test recommended in 24–48 hrs.";
    }
    if (data.length && data[0].value > OPTIMAL_HIGH) {
      return "First reading already above the optimal window — cycle may have advanced past prime.";
    }
    return null;
  }, [suggestions, data]);

  return (
    <div className="rounded-md border border-border bg-card p-3" data-testid="prog-chart">
      <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-2">
        Progesterone curve · ng/mL
      </p>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
            <YAxis
              domain={[0, yMax]}
              tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
              width={32}
              label={{ value: "ng/mL", angle: -90, position: "insideLeft", offset: 8, style: { fontSize: 10, fill: "hsl(var(--muted-foreground))" } }}
            />
            <Tooltip
              formatter={(v) => [`${v} ng/mL`, "progesterone"]}
              labelFormatter={(l, payload) => {
                const item = payload?.[0]?.payload;
                return item?.notes ? `${l} · ${item.notes}` : l;
              }}
              contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid hsl(var(--border))" }}
            />
            {/* Optimal breeding window band — drawn UNDER the reference lines
                so the labels stay legible. */}
            <ReferenceArea
              y1={OPTIMAL_LOW}
              y2={OPTIMAL_HIGH}
              fill="hsl(var(--primary))"
              fillOpacity={0.08}
              stroke="hsl(var(--primary))"
              strokeOpacity={0.2}
              label={{ value: "Optimal window · 10–20 ng/mL", position: "insideTopRight", fontSize: 9, fill: "hsl(var(--primary))" }}
            />
            {/* Clinical milestones */}
            <ReferenceLine y={2} stroke="#f59e0b" strokeDasharray="4 3" label={{ value: "LH surge (~2)", position: "insideTopLeft", fontSize: 9, fill: "#f59e0b" }} />
            <ReferenceLine y={5} stroke="#22c55e" strokeDasharray="4 3" label={{ value: "Ovulation (~5)", position: "insideTopLeft", fontSize: 9, fill: "#22c55e" }} />
            <ReferenceLine y={20} stroke="#3b82f6" strokeDasharray="4 3" label={{ value: "Fertilization (~20)", position: "insideBottomLeft", fontSize: 9, fill: "#3b82f6" }} />
            <Line
              type="monotone"
              dataKey="value"
              stroke="hsl(var(--primary))"
              strokeWidth={2.5}
              dot={{ r: 4, fill: "hsl(var(--primary))" }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {suggestionLine ? (
        <p
          className="mt-2 text-[12px] leading-snug bg-primary/5 border border-primary/20 rounded px-2.5 py-1.5 text-primary flex items-start gap-1.5"
          data-testid="prog-suggestion"
        >
          <span aria-hidden>🎯</span>
          <span>{suggestionLine}</span>
        </p>
      ) : null}
    </div>
  );
}

// -----------------------------------------------------------------
// CostBreakdownDonut — small donut chart summarizing cost spend
// across the category buckets. Renders only when there is at least
// one non-zero cost. Categories with zero spend are dropped.
// -----------------------------------------------------------------
const CATEGORY_COLORS = {
  progesterone_smear: "#f59e0b",  // amber
  vet_visit:          "#22c55e",  // green
  semen_shipping:     "#3b82f6",  // blue
  semen_collection:   "#a855f7",  // violet
  travel:             "#ec4899",  // pink
  surgical_ai:        "#ef4444",  // red
  other:              "#94a3b8",  // slate
};

function CostBreakdownDonut({ costs, total }) {
  const slices = useMemo(() => {
    const buckets = {};
    for (const c of costs || []) {
      const amt = Number(c.amount) || 0;
      if (amt <= 0) continue;
      const key = c.category || "other";
      buckets[key] = (buckets[key] || 0) + amt;
    }
    return Object.entries(buckets)
      .map(([key, value]) => ({ key, label: COST_LABELS[key] || key, value }))
      .sort((a, b) => b.value - a.value);
  }, [costs]);

  if (slices.length === 0 || total <= 0) {
    return (
      <div className="rounded-md border border-dashed border-border p-3 text-center text-xs text-muted-foreground" data-testid="cost-breakdown-empty">
        Add cost amounts to see a spend breakdown.
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3" data-testid="cost-breakdown-chart">
      <div className="h-32 w-32 shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={slices}
              dataKey="value"
              nameKey="label"
              innerRadius={30}
              outerRadius={55}
              paddingAngle={2}
              stroke="hsl(var(--background))"
              strokeWidth={2}
            >
              {slices.map((s) => (
                <Cell key={s.key} fill={CATEGORY_COLORS[s.key] || CATEGORY_COLORS.other} />
              ))}
            </Pie>
            <Tooltip
              formatter={(v, _n, ctx) => [fmtMoney(v), ctx?.payload?.label]}
              contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid hsl(var(--border))" }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="flex-1 min-w-0 space-y-1 text-[11px]" data-testid="cost-breakdown-legend">
        {slices.map((s) => {
          const pct = total > 0 ? Math.round((s.value / total) * 100) : 0;
          return (
            <li key={s.key} className="flex items-center gap-2 min-w-0" data-testid={`cost-breakdown-slice-${s.key}`}>
              <span
                className="h-2.5 w-2.5 rounded-full shrink-0"
                style={{ backgroundColor: CATEGORY_COLORS[s.key] || CATEGORY_COLORS.other }}
                aria-hidden
              />
              <span className="truncate flex-1 text-foreground/80">{s.label}</span>
              <span className="tabular-nums text-muted-foreground shrink-0">{fmtMoney(s.value)} · {pct}%</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
